import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'vibrant' | 'electric';
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'xxl';
  fullWidth?: boolean;
  href?: string;
  external?: boolean;
}
export function Button({
  children,
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  className = '',
  href,
  external,
  ...props
}: ButtonProps) {
  const baseStyles = 'inline-flex items-center justify-center rounded-full font-bold transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed uppercase tracking-wider';
  const variants = {
    primary: 'bg-pink-500 hover:bg-pink-600 text-white shadow-lg hover:shadow-xl focus:ring-pink-400 border border-pink-400/50',
    secondary: 'bg-purple-100 hover:bg-purple-200 text-purple-900 focus:ring-purple-400',
    outline: 'border-2 border-slate-300 hover:border-slate-400 text-slate-600 hover:text-slate-800 bg-transparent',
    vibrant: 'bg-gradient-to-r from-fuchsia-500 to-pink-500 hover:from-fuchsia-600 hover:to-pink-600 text-white shadow-lg shadow-fuchsia-500/50 hover:shadow-xl hover:shadow-fuchsia-500/60 focus:ring-fuchsia-400 border border-fuchsia-300',
    electric: 'bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white shadow-lg shadow-pink-500/50 hover:shadow-xl hover:shadow-pink-500/60 focus:ring-pink-400 border border-pink-300'
  };
  const sizes = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-6 py-3 text-base',
    lg: 'px-8 py-4 text-lg',
    xl: 'px-10 py-5 text-xl',
    xxl: 'px-12 py-6 text-2xl'
  };
  const classes = `
    ${baseStyles}
    ${variants[variant]}
    ${sizes[size]}
    ${fullWidth ? 'w-full' : ''}
    ${className}
  `;
  const content = <>
      {children}
      {(variant === 'primary' || variant === 'vibrant' || variant === 'electric') && <ArrowRight className="ml-2 h-5 w-5" data-id="element-308" />}
    </>;
  if (href) {
    if (external) {
      return <motion.a href={href} target="_blank" rel="noopener noreferrer" className={classes} whileHover={{
        scale: 1.05
      }} whileTap={{
        scale: 0.95
      }} data-id="element-309">
          {content}
        </motion.a>;
    }
    return <motion.a href={href} className={classes} whileHover={{
      scale: 1.05
    }} whileTap={{
      scale: 0.95
    }} onClick={e => {
      e.preventDefault();
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth'
        });
      }
    }} data-id="element-310">
        {content}
      </motion.a>;
  }
  return <motion.button className={classes} whileHover={{
    scale: 1.05
  }} whileTap={{
    scale: 0.95
  }} {...props} data-id="element-311">
      {content}
    </motion.button>;
}